import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { supabase } from '../../shared/supabase/supabaseClient';

type Options = {
  /** Si false, no suscribe (p. ej. sin branch). */
  enabled?: boolean;
  /** true = siempre activo con app visible (badge nav). false = solo con pantalla enfocada (KDS). */
  alwaysOn?: boolean;
};

/**
 * Realtime sobre hospitality.kitchen_tickets filtrado por branch_id.
 * Debounce 500 ms; fallback vía onChanged cuando el canal falla.
 */
export function useKitchenRealtime(
  branchId: string | null | undefined,
  onChanged: () => void,
  options?: Options,
) {
  const enabled = options?.enabled !== false && Boolean(branchId?.trim());
  const alwaysOn = options?.alwaysOn === true;
  const [focused, setFocused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(true);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  const active = enabled && (alwaysOn || focused);

  useEffect(() => {
    if (!active) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChangedRef.current();
      }, 500);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    const branch = branchId!.trim();
    const channelName = `kitchen-realtime-${branch}`;

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'hospitality',
            table: 'kitchen_tickets',
            filter: `branch_id=eq.${branch}`,
          },
          scheduleRefresh,
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
          }
        });
    })();

    const appSub = AppState.addEventListener('change', (state) => {
      visibleRef.current = state === 'active';
      if (state === 'active') scheduleRefresh();
      if (state !== 'active' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) scheduleRefresh();
        if (!visibleRef.current && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      visibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (channel) void supabase.removeChannel(channel);
      appSub.remove();
      removeVisibility?.();
    };
  }, [active, branchId]);
}
